package com.sweetlime.dungeonamigo.compendium

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.sweetlime.dungeonamigo.cache.Items
import com.sweetlime.dungeonamigo.cache.Monsters
import com.sweetlime.dungeonamigo.cache.Spells
import com.sweetlime.dungeonamigo.cache.SrdCacheManager
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

sealed class CompendiumUiState {
    data class Syncing(val category: String, val percent: Int) : CompendiumUiState()
    data object Ready : CompendiumUiState()
    data class Error(val message: String) : CompendiumUiState()
}

@OptIn(FlowPreview::class)
class CompendiumViewModel(
    private val repository: CompendiumRepository,
    private val cacheManager: SrdCacheManager
) : ViewModel() {

    private val _state = MutableStateFlow<CompendiumUiState>(CompendiumUiState.Syncing("", 0))
    val state = _state.asStateFlow()

    private val _spellQuery = MutableStateFlow("")
    private val _monsterQuery = MutableStateFlow("")
    private val _itemQuery = MutableStateFlow("")

    val spells: kotlinx.coroutines.flow.StateFlow<List<Spells>> = _spellQuery
        .debounce(300L)
        .map { repository.searchSpells(it) }
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    val monsters: kotlinx.coroutines.flow.StateFlow<List<Monsters>> = _monsterQuery
        .debounce(300L)
        .map { repository.searchMonsters(it) }
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    val items: kotlinx.coroutines.flow.StateFlow<List<Items>> = _itemQuery
        .debounce(300L)
        .map { repository.searchItems(it) }
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    init {
        viewModelScope.launch {
            try {
                if (!cacheManager.isSynced()) {
                    cacheManager.syncAll { category, percent ->
                        _state.value = CompendiumUiState.Syncing(category, percent)
                    }
                }
                _state.value = CompendiumUiState.Ready
            } catch (e: Exception) {
                _state.value = CompendiumUiState.Error(e.message ?: "Sync failed")
            }
        }
    }

    fun updateSpellQuery(query: String) { _spellQuery.value = query }
    fun updateMonsterQuery(query: String) { _monsterQuery.value = query }
    fun updateItemQuery(query: String) { _itemQuery.value = query }

    fun retry() {
        _state.value = CompendiumUiState.Syncing("", 0)
        viewModelScope.launch {
            try {
                cacheManager.syncAll { category, percent ->
                    _state.value = CompendiumUiState.Syncing(category, percent)
                }
                _state.value = CompendiumUiState.Ready
            } catch (e: Exception) {
                _state.value = CompendiumUiState.Error(e.message ?: "Sync failed")
            }
        }
    }
}